@if (isLoadingSessions()) {
  <app-loading-component
    [flavorTextOptions]="[
      'Gathering session ledgers from the game master table.',
      'Unrolling campaign notes and party rosters.',
      'Sharpening virtual pencils and rolling initiative.',
      'Consulting the ancient scrolls of session management.',
      'Summoning the spirits of past adventures to find your sessions.']"
  />
} @else {
  <section class="page-card">
    <app-back-button-component />
    
    <h2 class="section-title">Sessions</h2>

    @if (auth.hasRole(userRole.DungeonMaster)) {
      <div class="sessions-header-row">
        <p class="empty-copy">
          Showing sessions assigned to you as master.
        </p>
        <a class="primary-btn" [routerLink]="['/sessions/create']">
          Create Session
        </a>
      </div>
    }

    @if (loadSessionsError()) {
      <p class="validation-text">{{ loadSessionsError() }}</p>
    } @else if (!visibleSessions().length) {
      <p class="empty-copy">
        @if (auth.hasRole(userRole.DungeonMaster)) {
          No sessions are assigned to you as master yet.
        } @else {
          No sessions were returned from the backend.
        }
      </p>
    } @else {
      @if (joinSessionError()) {
        <p class="validation-text">{{ joinSessionError() }}</p>
      }

      @if (loadCampaignsError()) {
        <p class="validation-text">{{ loadCampaignsError() }}</p>
      }

      @if (loadUsersError()) {
        <p class="validation-text">{{ loadUsersError() }}</p>
      }

      <div class="list-grid">
        @for (session of visibleSessions(); track session.id) {
          <article class="list-card">
            @let campaign = getSessionCampaign(session);

            <div class="session-kicker">Session</div>
            <h3>{{ session.name || 'Unnamed Session' }}</h3>
            <p class="session-description">{{ session.description || 'No description was provided for this session.' }}</p>

            <a
              class="session-campaign-link"
              [routerLink]="['/campaigns', session.campaignId]"
              [queryParams]="{
                campaignName: session.campaignName || 'Unknown Campaign',
                sessionId: session.id
              }"
            >
              <span class="session-campaign-content">
                <span class="session-campaign-kicker">Campaign</span>
                <span class="session-campaign-title">{{ campaign.name }}</span>
                <span class="session-campaign-description">{{ campaign.description }}</span>
                <span class="session-campaign-universe">
                  {{ session.universeName || 'Unknown Universe' }}
                </span>
              </span>
              <span class="session-campaign-action" aria-hidden="true">&rarr;</span>
            </a>

            <div class="session-meta">
              <div><strong>Master:</strong> {{ getUserName(session.masterId) }}</div>
              <div><strong>Players:</strong> {{ session.players.length }} / {{ session.maxPlayers }}</div>
            </div>

            @if (auth.hasRole(userRole.DungeonMaster) && session.players.length) {
              <div class="session-player-list">
                <div class="section-kicker">Players</div>

                @for (player of session.players; track player.id || player.userId + player.characterId) {
                  <div class="session-player-row">
                    <span>{{ getUserName(player.userId) }}</span>

                    @if (player.characterId) {
                      <a
                        class="secondary-btn"
                        [routerLink]="['/characters', session.id, player.characterId]"
                      >
                        View Character
                      </a>
                    } @else {
                      <span class="session-state full-state">No character assigned</span>
                    }
                  </div>
                }
              </div>
            }

            @if (auth.hasRole(userRole.Player)) {
              <div class="session-action-row">
                @if (hasJoined(session)) {
                  <span class="session-state joined-state">You have already joined this session.</span>
                } @else if (isFull(session)) {
                  <span class="session-state full-state">This session has reached its player limit.</span>
                } @else {
                <button
                  type="button"
                  class="secondary-btn"
                  [disabled]="joiningSessionId() === session.id"
                  (click)="openJoinSession(session)"
                >
                  {{ joiningSessionId() === session.id ? 'Joining...' : 'Join Session' }}
                </button>
                }
              </div>
            }
          </article>
        }
      </div>
    }
  </section>
}

@if (joinTargetSession() || isPreparingJoin()) {
  <div class="join-modal-overlay">
    <section class="join-modal">
      <div class="section-kicker">Choose Character</div>
      <h3>Join {{ joinTargetSession()?.name || 'Session' }}</h3>
      <p class="empty-copy">
        Pick one of your existing characters from the same universe before joining this campaign.
      </p>

      @if (isPreparingJoin()) {
        <app-loading-component
          [flavorTextOptions]="[
            'Searching the universe for characters bearing your mark.',
            'Gathering your adventurers from neighboring sessions.',
            'Checking which of your heroes belong to this world.'
          ]"
        />
      } @else {
        @if (joinSessionError()) {
          <p class="validation-text">{{ joinSessionError() }}</p>
        }

        @if (joinCharacterOptions().length) {
          <label class="form-group">
            <span>Your Characters In This Universe</span>
            <select
              [ngModel]="selectedJoinCharacterId()"
              (ngModelChange)="selectedJoinCharacterId.set($event)"
            >
              <option value="">Choose a character</option>
              @for (option of joinCharacterOptions(); track option.character.id) {
                <option [value]="option.character.id">
                  {{ option.character.name || 'Unnamed Character' }} | {{ option.character.role || option.character.type || 'Unknown Role' }} | {{ option.sessionName || 'Unknown Session' }}
                </option>
              }
            </select>
          </label>

          <div class="button-row">
            <button
              type="button"
              class="primary-btn"
              [disabled]="!selectedJoinCharacterId() || joiningSessionId() === joinTargetSession()?.id"
              (click)="confirmJoinSession()"
            >
              {{
                joiningSessionId() === joinTargetSession()?.id
                  ? 'Joining Session...'
                  : 'Join With Selected Character'
              }}
            </button>
            <button type="button" class="secondary-btn" (click)="closeJoinSession()">
              Cancel
            </button>
          </div>
        } @else {
          <div class="session-state full-state">
            No owned character was found in {{ joinTargetSession()?.universeName || 'this universe' }} yet.
          </div>

          <div class="button-row">
            <button
              type="button"
              class="primary-btn"
              (click)="goToCharacterCreation()"
            >
              Create Character For This Universe
            </button>
            <button type="button" class="secondary-btn" (click)="closeJoinSession()">
              Cancel
            </button>
          </div>
        }
      }
    </section>
  </div>
}
